import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Card } from "../components/ui/Card/Card";
import { MessageStrip } from "../components/ui/MessageStrip/MessageStrip";
import { FormField } from "../components/composite/FormField/FormField";
import { Title } from "@ui5/webcomponents-react";
import { ROUTES } from "../constants/routes";
import { cn } from "../lib/cn";
import "@ui5/webcomponents-icons/dist/employee.js";
import "@ui5/webcomponents-icons/dist/locked.js";
import "@ui5/webcomponents-icons/dist/email.js";

export default function Signup() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { signup, error, clearError, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors on mount
  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    } else if (formData.firstName.length < 2) {
      errors.firstName = "First name must be at least 2 characters";
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    } else if (formData.lastName.length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
    }

    // Email validation
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signup(
        formData.firstName.trim(),
        formData.lastName.trim(),
        formData.email.trim(),
        formData.password,
        formData.confirmPassword,
      );

      if (result.success) {
        showSuccess("Account created successfully! Welcome to conRIO.");
        navigate(ROUTES.DASHBOARD, { replace: true });
      } else {
        showError(result.error || "Signup failed. Please try again.");
      }
    } catch (err) {
      console.error("Signup error:", err);
      showError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate password strength
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    const levels = [
      { strength: 0, label: "Too weak", color: "bg-red-500" },
      { strength: 1, label: "Weak", color: "bg-orange-500" },
      { strength: 2, label: "Fair", color: "bg-yellow-500" },
      { strength: 3, label: "Good", color: "bg-blue-500" },
      { strength: 4, label: "Strong", color: "bg-green-500" },
      { strength: 5, label: "Very Strong", color: "bg-green-600" },
    ];

    return levels[strength];
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in-up">
        {/* Header */}
        <div className="text-center mb-8">
          <Title level="H1" className="text-4xl font-bold text-gray-900 mb-2">
            Create Account
          </Title>
          <p className="text-gray-600">
            Join conRIO to track and analyze service changes
          </p>
        </div>

        {/* Signup Card */}
        <Card className="p-8 shadow-xl">
          {/* Error Message */}
          {error && (
            <MessageStrip design="Negative" className="mb-6">
              {error}
            </MessageStrip>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* First Name */}
            <FormField
              label="First Name"
              required
              error={validationErrors.firstName}
            >
              <Input
                value={formData.firstName}
                onInput={(e) => handleChange("firstName", e.target.value)}
                placeholder="Enter your first name"
                valueState={validationErrors.firstName ? "Error" : "None"}
                disabled={isSubmitting}
              />
            </FormField>

            {/* Last Name */}
            <FormField
              label="Last Name"
              required
              error={validationErrors.lastName}
            >
              <Input
                value={formData.lastName}
                onInput={(e) => handleChange("lastName", e.target.value)}
                placeholder="Enter your last name"
                valueState={validationErrors.lastName ? "Error" : "None"}
                disabled={isSubmitting}
              />
            </FormField>

            {/* Email */}
            <FormField label="Email" required error={validationErrors.email}>
              <Input
                type="Email"
                value={formData.email}
                onInput={(e) => handleChange("email", e.target.value)}
                placeholder="you@example.com"
                valueState={validationErrors.email ? "Error" : "None"}
                disabled={isSubmitting}
                icon="email"
              />
            </FormField>

            {/* Password */}
            <FormField
              label="Password"
              required
              error={validationErrors.password}
              helpText="At least 6 characters"
            >
              <Input
                type="Password"
                value={formData.password}
                onInput={(e) => handleChange("password", e.target.value)}
                placeholder="Create a strong password"
                valueState={validationErrors.password ? "Error" : "None"}
                disabled={isSubmitting}
                icon="locked"
              />
            </FormField>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="space-y-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={cn(
                        "h-1 flex-1 rounded-full transition-all duration-300",
                        level <= passwordStrength.strength
                          ? passwordStrength.color
                          : "bg-gray-200",
                      )}
                    />
                  ))}
                </div>
                <p
                  className={cn(
                    "text-sm font-medium",
                    passwordStrength.color.replace("bg-", "text-"),
                  )}
                >
                  {passwordStrength.label}
                </p>
              </div>
            )}

            {/* Confirm Password */}
            <FormField
              label="Confirm Password"
              required
              error={validationErrors.confirmPassword}
            >
              <Input
                type="Password"
                value={formData.confirmPassword}
                onInput={(e) => handleChange("confirmPassword", e.target.value)}
                placeholder="Confirm your password"
                valueState={validationErrors.confirmPassword ? "Error" : "None"}
                disabled={isSubmitting}
                icon="locked"
              />
            </FormField>

            {/* Submit Button */}
            <Button
              design="Emphasized"
              className="w-full"
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
            >
              {isSubmitting ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link
                to={ROUTES.LOGIN}
                className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
